import chineseLanguagePack from "@haxqer/ra-language-chinese";
import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { chineseCrmMessages } from "./chineseCrmMessages";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";

// The package is CJS (`exports.default = messages`). Vite's interop leaves
// that synthetic default in place, so the import is `{ default: messages }`.
const chineseMessages =
  (
    chineseLanguagePack as {
      default?: typeof chineseLanguagePack;
    }
  ).default ?? chineseLanguagePack;

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
    oauth: {
      authorize: "Authorize Application",
      authorize_details: "This application wants to access your account.",
      access_scope:
        "It will be able to read and modify your data, with the same permissions as your account.",
      approved: "Authorization Approved",
      close_tab: "You can now close this tab.",
      no_request: "No authorization request found",
    },
  },
};

// @haxqer/ra-language-chinese tracks react-admin 4. Fill keys added in ra 5.
const raChineseMessagesOverride = {
  ra: {
    action: {
      reset: "重置",
      search_columns: "搜索列",
      select_all_button: "全选",
    },
    auth: {
      email: "邮箱",
    },
    guesser: {
      empty: {
        title: "暂无数据",
        message: "请检查数据源",
      },
    },
    message: {
      access_denied: "你没有访问此页面的权限",
      authentication_error: "身份验证服务器返回错误，无法校验你的凭据。",
      placeholder_data_warning: "网络异常：数据刷新失败。",
      select_all_limit_reached:
        "条目过多，无法全部选择。仅选中了前 %{max} 条。",
    },
    navigation: {
      clear_filters: "清除筛选",
      no_filtered_results: "使用当前筛选条件未找到%{name}。",
    },
    notification: {
      offline: "网络未连接，无法获取数据。",
    },
    page: {
      access_denied: "拒绝访问",
      authentication_error: "身份验证错误",
      dashboard: "工作看板",
    },
    validation: {
      unique: "必须唯一",
    },
  },
};

// No Simplified Chinese package for ra-supabase. English messages stay the
// base; override the strings this app shows.
const raSupabaseChineseMessagesOverride = {
  "ra-supabase": {
    auth: {
      email: "邮箱",
      confirm_password: "确认密码",
      sign_in_with: "使用 %{provider} 登录",
      forgot_password: "忘记密码？",
      reset_password: "重置密码",
      password_reset: "请查收邮件中的重置密码消息。",
      missing_tokens: "缺少访问令牌和刷新令牌",
      back_to_login: "返回登录",
    },
    oauth: {
      authorize: "授权应用",
      authorize_details: "此应用请求访问你的账户。",
      access_scope: "它将能够读取和修改你的数据，权限与你的账户相同。",
      approved: "授权已通过",
      close_tab: "现在可以关闭此标签页。",
      no_request: "未找到授权请求",
    },
    reset_password: {
      forgot_password: "忘记密码？",
      forgot_password_details: "输入邮箱以获取说明。",
    },
    set_password: {
      new_password: "设置密码",
    },
    validation: {
      password_mismatch: "两次输入的密码不一致",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
    oauth: {
      authorize: "Autoriser l'application",
      authorize_details: "Cette application souhaite accéder à votre compte.",
      access_scope:
        "Elle pourra lire et modifier vos données, avec les mêmes droits que votre compte.",
      approved: "Autorisation accordée",
      close_tab: "Vous pouvez fermer cet onglet.",
      no_request: "Aucune demande d'autorisation trouvée",
    },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
);

const chineseCatalog = mergeTranslations(
  englishCatalog,
  chineseMessages,
  raChineseMessagesOverride,
  raSupabaseChineseMessagesOverride,
  chineseCrmMessages,
);

export const getInitialLocale = (): "en" | "fr" | "zh" => {
  if (typeof navigator === "undefined") {
    return "en";
  }

  const browserLocale = navigator.languages?.[0] ?? navigator.language;
  const normalizedLocale = browserLocale?.toLowerCase() ?? "";
  if (normalizedLocale.startsWith("fr")) {
    return "fr";
  }
  if (normalizedLocale.startsWith("zh")) {
    return "zh";
  }

  return "en";
};

export const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "fr") {
      return frenchCatalog;
    }
    if (locale === "zh") {
      return chineseCatalog;
    }
    return englishCatalog;
  },
  getInitialLocale(),
  [
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
    { locale: "zh", name: "简体中文" },
  ],
  { allowMissing: true },
);

export const testI18nProvider = polyglotI18nProvider(
  () => englishCatalog,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
